import React, {
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import type {
  FileResultDTO,
} from 'dtos/fileResultDTO';
import type {
  MultiMediaResponse,
} from 'dtos/multiMediaResponse';
import type {
  ReportOutputDTO,
} from 'dtos/reportOutputDTO';
import type {
  ReportOutputFilePreviewDTO,
} from 'dtos/reportOutputFilePreviewDTO';
import {
  getApiBillingDownloadBulkPatientStatementOutputFile,
  getApiBillingGetBulkPatientStatementOutput,
  getApiBillingGetBulkPatientStatementOutputFilePreviewUrl,
  getApiReportDownloadReportOutputFile,
  getApiReportGetReportOutput,
  getApiReportGetReportOutputFilePreviewUrl,
} from 'endpoints';
import {
  useMixpanelTrack,
} from 'core/hooks';
import {
  FileViewerContext,
} from 'pages/Dashboard/contexts/fileViewer';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  downloadFile,
} from 'pages/Dashboard/utils/helper';
import {
  isRunOutputMockEnabled,
  mockRunEndpoints,
} from 'views/EMR/components/Panel/RunOutputPanel/mockRunOutput';
import type {
  RunOutput,
  RunOutputFile,
  RunOutputSource,
} from 'views/EMR/components/Panel/RunOutputPanel/types';
import {
  getRunId,
  getRunParams,
  normalizeRunOutput,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';

type FileId = number | null | undefined;

type RunEndpoints = {
  getOutput: () => Promise<ReportOutputDTO>;
  downloadFile: (reportOutputFileId: FileId) => Promise<FileResultDTO>;
  getPreviewUrl: (reportOutputFileId: FileId) => Promise<ReportOutputFilePreviewDTO>;
};

// Picks the report or billing endpoint for the run's kind.
function getRunEndpoints(source: RunOutputSource): RunEndpoints {
  if (isRunOutputMockEnabled()) {
    return mockRunEndpoints(source);
  }
  if (source.kind === 'report') {
    return {
      getOutput: () => getApiReportGetReportOutput(getRunParams(source)),
      downloadFile: (fileId) => getApiReportDownloadReportOutputFile(getRunParams(source, fileId)),
      getPreviewUrl: (fileId) => getApiReportGetReportOutputFilePreviewUrl(getRunParams(source, fileId)),
    };
  }
  return {
    getOutput: () => getApiBillingGetBulkPatientStatementOutput(getRunParams(source)),
    downloadFile: (fileId) => getApiBillingDownloadBulkPatientStatementOutputFile(getRunParams(source, fileId)),
    getPreviewUrl: (fileId) => getApiBillingGetBulkPatientStatementOutputFilePreviewUrl(getRunParams(source, fileId)),
  };
}

export function useRunOutput(source: RunOutputSource, { enabled = true } = {}) {
  return useQuery<ReportOutputDTO, Error, RunOutput>({
    queryKey: ['run-output', source.kind, getRunId(source)],
    queryFn: () => getRunEndpoints(source).getOutput(),
    select: normalizeRunOutput,
    enabled,
  });
}

type DownloadVariables = {
  reportOutputFileId?: number | null;
};

// `pendingFileId` is the file being downloaded (null = the run's first file), or undefined when idle.
export function useDownloadRunOutputFile(source: RunOutputSource) {
  const mixPanelTrack = useMixpanelTrack();
  const mutation = useMutation<FileResultDTO, Error, DownloadVariables>({
    mutationFn: ({ reportOutputFileId }) => getRunEndpoints(source).downloadFile(reportOutputFileId),
    onSuccess: (result) => {
      downloadFile(result);
      if (source.kind === 'report') {
        mixPanelTrack('downloadReport', { reportId: source.reportId });
      }
    },
  });

  return {
    ...mutation,
    pendingFileId: mutation.isPending
      ? mutation.variables?.reportOutputFileId ?? null
      : undefined,
  };
}

export function useDownloadAllRunOutputFiles(source: RunOutputSource) {
  const { mutateAsync } = useDownloadRunOutputFile(source);
  const [isPending, setIsPending] = useState(false);

  // No zip endpoint yet, so each file downloads separately; replace with a zip endpoint if the backend adds one
  const downloadAll = useCallback(async (files: RunOutputFile[]) => {
    setIsPending(true);
    try {
      for (const { reportOutputFileId } of files) {
        try {
          // Sequential on purpose: browsers block many simultaneous downloads.
          // eslint-disable-next-line no-await-in-loop
          await mutateAsync({ reportOutputFileId });
        } catch {
          // The global mutation error toast reports the failure; keep going with the rest.
        }
      }
    } finally {
      setIsPending(false);
    }
  }, [mutateAsync]);

  return { downloadAll, isPending };
}

// `pendingFileId` is the file whose preview is loading (null = the run's first file), or undefined when idle.
export function usePreviewRunOutputFile(source: RunOutputSource) {
  const { openDialog } = useContext(FileViewerContext);
  // Never cached: the preview URL expires after 10 minutes, so every click fetches a fresh one.
  const mutation = useMutation<string, Error, RunOutputFile>({
    mutationFn: async ({ reportOutputFileId }) => {
      const { url } = await getRunEndpoints(source).getPreviewUrl(reportOutputFileId);
      if (!url) {
        throw new Error('Preview is not available for this file.');
      }
      return url;
    },
    onSuccess: (url, file) => {
      openDialog({
        title: file.fileName,
        content: (
          <MediaPreview
            media={{ fileName: file.fileName, url: { value: url } } as MultiMediaResponse}
          />
        ),
      });
    },
  });

  return {
    preview: mutation.mutate,
    pendingFileId: mutation.isPending
      ? mutation.variables?.reportOutputFileId ?? null
      : undefined,
  };
}
